import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import API_BASE_URL from '../../../config/api';
import { RiLockPasswordLine, RiEyeLine, RiEyeOffLine } from 'react-icons/ri';

const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        new_password: '',
        confirm_password: ''
    });
    const [loading, setLoading] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Get uid and token from URL
    const uid = searchParams.get('uid');
    const token = searchParams.get('token');

    useEffect(() => {
        if (!uid || !token) {
            Swal.fire({
                icon: 'error',
                title: 'Invalid Link',
                text: 'The password reset link is invalid or expired.',
                allowOutsideClick: false,
                confirmButtonText: 'Go to Login'
            }).then(() => {
                navigate('/');
            });
        }
    }, [uid, token, navigate]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (formData.new_password.length < 6) {
            Swal.fire('Error', 'Password must be at least 6 characters long.', 'error');
            return;
        }

        if (formData.new_password !== formData.confirm_password) {
            Swal.fire('Error', 'Passwords do not match.', 'error');
            return;
        }

        setLoading(true);

        try {
            await axios.post(`${API_BASE_URL}/api/v1/users/reset-password/`, {
                uid: uid,
                token: token,
                new_password: formData.new_password,
                re_new_password: formData.confirm_password
            });

            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Your password has been reset successfully.',
                confirmButtonText: 'Login Now'
            }).then(() => {
                navigate('/');
            });

        } catch (error) {
            console.error('Reset password error:', error);
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.uid ||
                error.response?.data?.token ||
                error.response?.data?.new_password ||
                'Failed to reset password. The link may have expired.';

            Swal.fire('Error', Array.isArray(errorMessage) ? errorMessage[0] : errorMessage, 'error');
        } finally {
            setLoading(false);
        }
    };

    if (!uid || !token) return null;

    return (
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '100vh', backgroundColor: '#f3f4f6' }}>
            <div className="card shadow-lg border-0" style={{ maxWidth: '400px', width: '100%' }}>
                <div className="card-body p-4">
                    <div className="text-center mb-4">
                        <div className="avatar avatar-lg bg-primary-subtle text-primary rounded-circle mb-3 d-flex align-items-center justify-content-center mx-auto" style={{ width: '60px', height: '60px' }}>
                            <RiLockPasswordLine size={28} />
                        </div>
                        <h4 className="fw-bold text-dark">Reset Password</h4>
                        <p className="text-muted small">Enter your new password below.</p>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="mb-3">
                            <label className="form-label text-start d-block fw-medium">New Password</label>
                            <div className="input-group">
                                <input
                                    type={showNewPassword ? "text" : "password"}
                                    className="form-control"
                                    name="new_password"
                                    placeholder="Min 6 characters"
                                    value={formData.new_password}
                                    onChange={handleChange}
                                    required
                                />
                                <button
                                    className="btn btn-outline-secondary"
                                    type="button"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                >
                                    {showNewPassword ? <RiEyeOffLine /> : <RiEyeLine />}
                                </button>
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="form-label text-start d-block fw-medium">Confirm Password</label>
                            <div className="input-group">
                                <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    className="form-control"
                                    name="confirm_password"
                                    placeholder="Confirm new password"
                                    value={formData.confirm_password}
                                    onChange={handleChange}
                                    required
                                />
                                <button
                                    className="btn btn-outline-secondary"
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                >
                                    {showConfirmPassword ? <RiEyeOffLine /> : <RiEyeLine />}
                                </button>
                            </div>
                        </div>

                        <button
                            type="submit"
                            className="btn btn-primary w-100 py-2 fw-bold"
                            disabled={loading}
                        >
                            {loading ? 'Resetting...' : 'Reset Password'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;